import { memo, useCallback, useContext } from "react";
import "./SmartDPHotelDescription.scss";
import AppGlobalsContext from "app/AppGlobalsContext";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import ProductNameWithCategory from "app/pages/.shared/ProductNameWithCategory/ProductNameWithCategory";
import PropTypes from "prop-types";
import { DESTINATION_USA_CODE, PRODUCT_PAGE_CODE_STATUS } from "app/constants";
import SmartDPHotelDescriptionLoader from "app/pages/SmartDP/Quote/HotelDescription/SmartDPHotelDescriptionLoader";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import HotelPhotoSection from "app/pages/SmartDP/Quote/HotelDescription/HotelPhotoSection";
import HotelHeader from "app/pages/SmartDP/Quote/HotelDescription/HotelHeader";
import HotelDetails from "app/pages/SmartDP/Quote/HotelDescription/HotelDetails";
import HotelSections from "app/pages/SmartDP/Quote/HotelDescription/HotelSections";
import { useSlideshow } from "app/components/ui/SlideShow/useSlideshow";

const SmartDPHotelDescription = ({ hotel = {}, printMode, specialOfferInfos = {} }) => {
	const { resolution } = useContext(AppGlobalsContext);

	const { track } = useAnalytics();

	const { isSlideshowOpened, openSlideshow, closeSlideshow } = useSlideshow();

	const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;

	const tripAdvisorData = hotel.tripAdvisor || {};
	const holidayCheckData = hotel.holidaycheck || {};

	const openPhotosSlideshow = useCallback(() => {
		track("open_photos_carousel_desktop_click");
		openSlideshow();
	}, []);

	const handleProductDetailsExpand = useCallback(event => {
		const label = event.target.textContent;

		track("expand_product_details", {
			detailsTitle: label,
		});
	}, []);

	const hotelName = <ProductNameWithCategory category={hotel.category} name={hotel.name} />;

	const warningMessage =
		hotel?.geography?.levels
			?.map(level => level?.code)
			.some(code => DESTINATION_USA_CODE.includes(code)) || false;

	return hotel.status === PRODUCT_PAGE_CODE_STATUS.LOADING ? (
		<div className="sdp-hotel-description">
			<SmartDPHotelDescriptionLoader />
		</div>
	) : (
		<div className="sdp-hotel-description">
			<HotelPhotoSection
				isMobile={isMobile}
				printMode={printMode}
				photos={hotel.photos}
				isSlideshowOpened={isSlideshowOpened}
				openSlideshow={openSlideshow}
				closeSlideshow={closeSlideshow}
				resolution={resolution}
			/>

			<div className="sdp-hotel-description__body">
				<HotelHeader
					hotelName={hotelName}
					tripAdvisorData={tripAdvisorData}
					holidayCheckData={holidayCheckData}
					status={hotel.status}
				/>
			</div>
			<HotelDetails
				isMobile={isMobile}
				printMode={printMode}
				photos={hotel.photos}
				resolution={resolution}
				hotelSummary={hotel.summary}
				openPhotosSlideshow={openPhotosSlideshow}
			/>
			<HotelSections
				printMode={printMode}
				specialOfferInfos={specialOfferInfos}
				hotel={hotel}
				handleProductDetailsExpand={handleProductDetailsExpand}
				warningMessage={warningMessage}
				facilityGroups={hotel.facilityGroups}
			/>
		</div>
	);
};

SmartDPHotelDescription.propTypes = {
	printMode: PropTypes.bool,
	specialOfferInfos: PropTypes.object,
	hotel: PropTypes.object,
};

export default memo(SmartDPHotelDescription);
